import React from 'react';
import { useNavigate } from 'react-router-dom';
import CosmicIcon from '../../common/CosmicIcon';
import './CosmicShareWithCoach.css';

/**
 * Cosmic "next step" coaching section on the results page.
 *
 * Two states:
 *  - If the taker arrived through a coach's shared link (we stash the coach in
 *    localStorage as `cosmic-coach-info`, or the saved result carries a
 *    coachCode), reassure them their coach already has access.
 *  - Otherwise, invite them to explore guided support and send them to the
 *    in-app coach directory.
 */
function getReferringCoachName() {
  try {
    const raw = localStorage.getItem('cosmic-coach-info');
    if (!raw) return null;
    const info = JSON.parse(raw);
    const first = info.firstName || info.first_name || '';
    const last = info.lastName || info.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || info.name || info.displayName || null;
  } catch (_) {
    return null;
  }
}

const CosmicShareWithCoach = ({ results }) => {
  const navigate = useNavigate();
  const coachName = getReferringCoachName();
  const hasCoach = Boolean(coachName) || Boolean(results?.coachCode);
  const coachLabel = coachName || 'Your coach';

  if (hasCoach) {
    return (
      <div className="cosmic-share-coach">
        <div className="cosmic-card cosmic-card--professional cosmic-coach-cta">
          <div className="cosmic-card__body cosmic-coach-cta__body">
            <div className="cosmic-coach-cta__icon">
              <CosmicIcon name="nav_coach" size={56} decorative />
            </div>
            <h2 className="cosmic-coach-cta__title">
              {coachLabel} has access to your results
            </h2>
            <p className="cosmic-coach-cta__text">
              {coachLabel} can already see your full constellation and is looking
              forward to going through it with you soon. The most useful thing you can
              do now is jot down the questions on your mind, because the real value
              comes from talking it through together.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cosmic-share-coach">
      <div className="cosmic-card cosmic-card--professional cosmic-coach-cta">
        <div className="cosmic-card__body cosmic-coach-cta__body">
          <div className="cosmic-coach-cta__icon">
            <CosmicIcon name="nav_coach" size={56} decorative />
          </div>
          <h2 className="cosmic-coach-cta__title">Your next step is guided support</h2>
          <p className="cosmic-coach-cta__text">
            Your results map out what drives you. A career coach helps you make sense of
            it and turn it into a plan that fits your life, so the insight you just
            unlocked actually carries you somewhere. A good coach has walked this with
            hundreds of people and can spot the openings you might miss on your own.
          </p>
          <ul className="cosmic-coach-cta__benefits">
            <li>Hear your results read back to you by someone trained to interpret them</li>
            <li>Build a real plan for your next move instead of guessing at it</li>
            <li>Move faster with a guide who has done this many times before</li>
          </ul>
          <button
            className="cosmic-cta cosmic-cta--lg"
            onClick={() => navigate('/coach-directory')}
          >
            Explore Career Coaching
          </button>
          <p className="cosmic-coach-cta__note">
            Browse coaches who turn results like yours into a clear path forward.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CosmicShareWithCoach;
